import { useState, useCallback } from 'react';
import {
  Box,
  Stack,
  Skeleton,
  Alert,
  Typography,
  Divider,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';
import { ContentCopy, Check, ErrorOutline } from '@mui/icons-material';
import CancelIcon from '@mui/icons-material/Cancel';
import { PermissionGate, Permissions } from '@billda/permissions-ui';
import { FeatureSwitchGate, Features } from '@billda/features-ui';
import { useAllocationDetails } from '../../../hooks/useAllocationDetails';
import { useSitePanelState } from '../../../hooks/useSitePanelState';
import { formatCurrency } from '../../../../../shared/utils/currency';
import {
  formatDate,
  formatDateTime,
} from '../../../../../shared/utils/dateFormatting';
import { PagedActivityTimeline } from '../molecules/PagedActivityTimeline';
import { useAllocationEventsTimeline } from '../../../hooks/useAllocationEventsTimeline';
import { VariationImageGallery } from './VariationImageGallery';
import { BookingImageGallery } from '../../../../bookings/components/BookingImageGallery';
import { InfoRow } from '../atoms/InfoRow';
import { AllocationStatusBadge } from '../atoms/AllocationStatusBadge';

interface AllocationDetailsPanelProps {
  allocationId: string;
  onAdminCancel?: (allocationId: string) => void;
}

export function AllocationDetailsPanel({
  allocationId,
  onAdminCancel,
}: AllocationDetailsPanelProps) {
  const { data: allocation, error } = useAllocationDetails(allocationId);
  const { siteId, plotId } = useSitePanelState();
  const siteIdNum = siteId ? parseInt(siteId, 10) : null;
  const plotIdNum = plotId ? parseInt(plotId, 10) : null;

  const {
    events,
    totalCount: timelineTotalCount,
    hasMore: timelineHasMore,
    isLoading: timelineLoading,
    isLoadingMore: timelineLoadingMore,
    loadMore: timelineLoadMore,
  } = useAllocationEventsTimeline(siteIdNum, plotIdNum, allocationId);

  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>(
    'idle'
  );

  const handleCopy = useCallback(async (text: string) => {
    setCopyState('idle');
    try {
      await navigator.clipboard.writeText(text);
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      setCopyState('error');
      setTimeout(() => setCopyState('idle'), 2000);
    }
  }, []);

  if (error && !allocation) {
    return (
      <Box data-testid="allocation-details-panel" sx={{ p: 3 }}>
        <Alert severity="error">
          {error instanceof Error
            ? error.message
            : 'Failed to load allocation details'}
        </Alert>
      </Box>
    );
  }

  if (!allocation) {
    return (
      <Box data-testid="allocation-details-panel" sx={{ p: 3 }}>
        <Box data-testid="skeleton-loader">
          <Stack spacing={2}>
            <Skeleton variant="rectangular" height={80} />
            <Skeleton variant="rectangular" height={120} />
            <Skeleton variant="rectangular" height={300} />
          </Stack>
        </Box>
      </Box>
    );
  }

  const isGang = allocation.worker.type === 'Gang';
  const isVariation = allocation.relatedItem.itemType === 'VariationRequest';
  const itemTypeLabel =
    allocation.relatedItem.itemType === 'LabourItem'
      ? 'Labour Item'
      : 'Variation Request';

  return (
    <Box data-testid="allocation-details-panel">
      <Box sx={{ p: 3, pb: 0 }}>
        <Typography variant="h6" sx={{ lineHeight: 1.3 }}>
          {allocation.relatedItem.itemName}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ flex: 1, minWidth: 0 }}
          >
            {itemTypeLabel}
          </Typography>
          <AllocationStatusBadge status={allocation.status} />
        </Box>
      </Box>

      <Box sx={{ p: 3 }}>
        <Stack spacing={0}>
          <Typography
            variant="overline"
            color="text.primary"
            sx={{ display: 'block', mb: 1, fontWeight: 600 }}
          >
            Booking
          </Typography>

          {allocation.uniqueReference && (
            <InfoRow label="Reference" testId="unique-reference">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  component="span"
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    fontWeight: 600,
                    letterSpacing: 0.5,
                  }}
                >
                  {allocation.uniqueReference}
                </Typography>
                <Tooltip
                  title={
                    copyState === 'copied'
                      ? 'Copied!'
                      : copyState === 'error'
                        ? 'Failed to copy'
                        : 'Copy reference'
                  }
                >
                  <IconButton
                    size="small"
                    onClick={() => handleCopy(allocation.uniqueReference!)}
                    data-testid="copy-reference-button"
                  >
                    {copyState === 'copied' ? (
                      <Check fontSize="small" color="success" />
                    ) : copyState === 'error' ? (
                      <ErrorOutline fontSize="small" color="error" />
                    ) : (
                      <ContentCopy fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            </InfoRow>
          )}

          <InfoRow label="Worker" testId="worker-name">
            {allocation.worker.displayName}
          </InfoRow>
          {isGang &&
            allocation.worker.memberNames &&
            allocation.worker.memberNames.length > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                data-testid="member-names"
                sx={{ display: 'block', mb: 1.5, mt: -1 }}
              >
                {allocation.worker.memberNames.join(', ')}
              </Typography>
            )}

          <InfoRow label="Work Date" testId="work-date">
            {formatDate(allocation.workDate)}
          </InfoRow>
          <InfoRow label="Amount" testId="amount">
            {formatCurrency(allocation.amount)}
          </InfoRow>
          <InfoRow label={itemTypeLabel} testId="related-item">
            {allocation.relatedItem.itemName}
          </InfoRow>
          {allocation.relatedItem.itemCode && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: -1, mb: 1.5 }}
            >
              {allocation.relatedItem.itemCode}
            </Typography>
          )}

          <Divider sx={{ my: 2 }} />
          <Typography
            variant="overline"
            color="text.primary"
            sx={{ display: 'block', mb: 1, fontWeight: 600 }}
          >
            Location
          </Typography>
          <InfoRow label="Site" testId="location-site">
            {allocation.plotContext.siteName}
          </InfoRow>
          <InfoRow label="Plot" testId="location-plot">
            {allocation.plotContext.plotName}
          </InfoRow>
          <InfoRow label="Plot Number" testId="location-plot-number">
            {allocation.plotContext.plotNumber}
          </InfoRow>

          {allocation.approval && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1,
                }}
              >
                <Typography
                  variant="overline"
                  color="text.primary"
                  sx={{ display: 'block', fontWeight: 600 }}
                >
                  Review
                </Typography>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: allocation.approval.isApproved
                      ? 'success.main'
                      : 'error.main',
                    flexShrink: 0,
                  }}
                />
              </Box>
              <InfoRow label="Reviewed by" testId="reviewed-by">
                {allocation.approval.reviewedByName}
              </InfoRow>
              <InfoRow label="Date" testId="reviewed-at">
                {formatDateTime(allocation.approval.reviewedAt)}
              </InfoRow>
              {!allocation.approval.isApproved &&
                allocation.approval.rejectionReason && (
                  <InfoRow label="Reason" testId="rejection-reason" multiline>
                    {allocation.approval.rejectionReason}
                  </InfoRow>
                )}
              {!allocation.approval.isApproved &&
                allocation.approval.rejectionNotes && (
                  <InfoRow label="Notes" testId="rejection-notes" multiline>
                    {allocation.approval.rejectionNotes}
                  </InfoRow>
                )}
            </>
          )}

          {isVariation && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography
                variant="overline"
                color="text.primary"
                sx={{ display: 'block', mb: 1, fontWeight: 600 }}
              >
                Photos
              </Typography>
              <VariationImageGallery
                variationRequestId={allocation.relatedItem.itemId}
                minimal
              />
            </>
          )}

          {!isVariation && (
            <FeatureSwitchGate
              feature={Features.Bookings.IsBookingImagesEnabled}
            >
              <PermissionGate
                permission={Permissions.Labour.BookingImages.Read}
              >
                <Divider sx={{ my: 2 }} />
                <Typography
                  variant="overline"
                  color="text.primary"
                  sx={{ display: 'block', mb: 1, fontWeight: 600 }}
                >
                  Photos
                </Typography>
                <BookingImageGallery allocationId={allocationId} minimal />
              </PermissionGate>
            </FeatureSwitchGate>
          )}

          {onAdminCancel &&
            !allocation.hasPotSubmission &&
            (allocation.status === 'Booked' ||
              allocation.status === 'Rejected') && (
              <PermissionGate permission={Permissions.Allocations.Admin.Cancel}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CancelIcon />}
                  fullWidth
                  onClick={() => onAdminCancel(allocationId)}
                  data-testid="admin-cancel-allocation-button"
                  sx={{ mt: 2 }}
                >
                  Cancel Allocation
                </Button>
              </PermissionGate>
            )}

          <Divider sx={{ my: 2 }} />
          <Typography
            variant="overline"
            color="text.primary"
            sx={{ display: 'block', mb: 1.5, fontWeight: 600 }}
          >
            Activity
          </Typography>
          <PagedActivityTimeline
            events={events}
            totalCount={timelineTotalCount}
            hasMore={timelineHasMore}
            isLoading={timelineLoading}
            isLoadingMore={timelineLoadingMore}
            loadMore={timelineLoadMore}
          />
        </Stack>
      </Box>
    </Box>
  );
}
